import type {
  OpsCapacityAssumptions,
  OpsCapacityIncident,
  OpsCapacityKind,
  OpsCapacityRates,
} from './types';

/** Every date on the tab is a Europe/London calendar day; stated on the page. */
export const OPS_TIMEZONE = 'Europe/London';

export const KINDS: OpsCapacityKind[] = [
  'phone',
  'web',
  'interventions',
  'mfa',
];

export const KIND_LABEL: Record<OpsCapacityKind, string> = {
  phone: 'Phone tasks',
  web: 'Web tasks',
  interventions: 'Browser interventions',
  mfa: 'MFA requests',
};

export const KIND_COLOR: Record<OpsCapacityKind, string> = {
  phone: 'rgb(59, 130, 246)',
  web: 'rgb(16, 185, 129)',
  interventions: 'rgb(245, 158, 11)',
  mfa: 'rgb(168, 85, 247)',
};

export const NO_DATA = '—';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoDate = (v: string | null | undefined): v is string => {
  if (!v || !DATE_RE.test(v)) return false;
  const parsed = new Date(`${v}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v
  );
};

/** Today's calendar date in London, independent of the browser's timezone. */
export function londonToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: OPS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** London calendar date of a UTC instant, for placing incidents on day axes. */
export function londonDateOf(iso: string): string {
  return londonToday(new Date(iso));
}

/** Monday (YYYY-MM-DD) of the ISO week containing a YYYY-MM-DD date. */
export function isoWeekStart(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export type RangeProblem =
  | 'invalid'
  | 'reversed'
  | 'future'
  | 'too-long'
  | null;

export const MAX_RANGE_DAYS = 730;

export function validateRange(
  from: string,
  to: string,
  today: string = londonToday(),
): RangeProblem {
  if (!isIsoDate(from) || !isIsoDate(to)) return 'invalid';
  if (from > to) return 'reversed';
  if (to > today) return 'future';
  const days =
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
      86_400_000 +
    1;
  return days > MAX_RANGE_DAYS ? 'too-long' : null;
}

export const RANGE_PROBLEM_TEXT: Record<Exclude<RangeProblem, null>, string> = {
  invalid: 'Enter both dates as YYYY-MM-DD.',
  reversed: 'The From date must be on or before the To date.',
  future: `The To date cannot be after today (${OPS_TIMEZONE}).`,
  'too-long': `Ranges are capped at ${MAX_RANGE_DAYS} days.`,
};

export const fmtInt = (v: number | null | undefined) =>
  v == null ? NO_DATA : v.toLocaleString();

export const fmtPct = (v: number | null | undefined, digits = 0) =>
  v == null ? NO_DATA : `${v.toFixed(digits)}%`;

export const fmt1 = (v: number | null | undefined) =>
  v == null
    ? NO_DATA
    : v.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

/** Minutes → "45m" / "2h 15m" / "3d 4h". */
export const fmtMinutes = (v: number | null | undefined) => {
  if (v == null) return NO_DATA;
  if (v < 1) return '<1m';
  const totalMinutes = Math.round(v);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours < 48) {
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
};

/** "2026-08-31" → "31 Aug". */
export const fmtDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

/** "2026-08-31" → "31 Aug 2026". */
export const fmtDayLong = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** UTC instant → "30 Aug 15:00 London". */
export const fmtLondonInstant = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: OPS_TIMEZONE,
  });

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const weekdayLabel = (isoWeekday: number) =>
  WEEKDAY_SHORT[isoWeekday - 1] ?? `${isoWeekday}`;

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  pl: 'Polish',
  ro: 'Romanian',
  tr: 'Turkish',
  ar: 'Arabic',
  hi: 'Hindi',
  ja: 'Japanese',
  zh: 'Chinese',
  unknown: 'Unknown',
};

export const languageLabel = (code: string) =>
  LANGUAGE_NAMES[code] ?? code.toUpperCase();

export const humanize = (key: string) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

export interface CapacityProjection {
  wau: number;
  items: Record<OpsCapacityKind, number>;
  operatorHoursPerWeek: number;
  averageFte: number;
  peakDayFte: number;
}

/** Same arithmetic as dream-be lib/opsCapacityMetrics.ts projectCapacity. */
export function projectCapacity(
  rates: OpsCapacityRates,
  assumptions: OpsCapacityAssumptions,
  wau: number,
): CapacityProjection {
  const items = {
    phone: Math.round(rates.phone * wau),
    web: Math.round(rates.web * wau),
    interventions: Math.round(rates.interventions * wau),
    mfa: Math.round(rates.mfa * wau),
  };
  const minutes = KINDS.reduce(
    (s, k) => s + items[k] * assumptions.handleMinutes[k],
    0,
  );
  const operatorHoursPerWeek = minutes / 60;
  const averageFte =
    assumptions.productiveHoursPerWeek > 0
      ? operatorHoursPerWeek / assumptions.productiveHoursPerWeek
      : 0;
  return {
    wau,
    items,
    operatorHoursPerWeek,
    averageFte,
    peakDayFte: averageFte * assumptions.peakDayFactor,
  };
}

export interface IncidentBand {
  label: string;
  note: string;
  /** Inclusive index range on the chart's category axis. */
  from: number;
  to: number;
}

/**
 * Map incidents onto a category axis whose labels are London dates (daily) or
 * ISO week Mondays (weekly). Incidents entirely outside the axis are dropped;
 * partially overlapping ones are clipped to the visible labels.
 */
export function incidentBands(
  incidents: OpsCapacityIncident[],
  axisDates: string[],
  granularity: 'day' | 'week',
): IncidentBand[] {
  if (axisDates.length === 0) return [];
  const key = (iso: string) => {
    const day = londonDateOf(iso);
    return granularity === 'week' ? isoWeekStart(day) : day;
  };
  return incidents.flatMap((inc) => {
    const startKey = key(inc.start);
    const endKey = key(inc.end);
    const from = axisDates.findIndex((d) => d >= startKey);
    if (from === -1) return [];
    let to = -1;
    for (let i = axisDates.length - 1; i >= 0; i--) {
      if (axisDates[i] <= endKey) {
        to = i;
        break;
      }
    }
    if (to === -1 || to < from) return [];
    return [{ label: inc.label, note: inc.note, from, to }];
  });
}
